// Espelha as regras por tipo de dia do cálculo de ponto (NiveisHoraExtraRegra e
// ToleranciaPontoRegra no mbs-api). Valores em minutos, salvo indicação.
export const SITUACOES_DIA = [
  "TODOS",
  "DIA_UTIL",
  "SABADO",
  "DOMINGO",
  "FERIADO",
];

export const TIPOS_HORA_EXTRA = ["EXTRA50", "EXTRA60", "EXTRA70", "EXTRA100"];

export const PERCENTUAL_TIPO = {
  EXTRA50: 50,
  EXTRA50INCOMPENSAVEL: 50,
  EXTRA60: 60,
  EXTRA60INCOMPENSAVEL: 60,
  EXTRA70: 70,
  EXTRA70INCOMPENSAVEL: 70,
  EXTRA100: 100,
  // Hora extra 100% de feriado que o sindicato apura em item próprio (FERIADOTRABALHADO)
  FERIADO: 100,
};

export const TOLERANCIA_PADRAO_MINUTOS = 10;
export const TOLERANCIA_MAXIMA_MINUTOS = 10;
export const TOLERANCIA_MARCACAO_MINUTOS = 5;

const INCOMPENSAVEL = {
  EXTRA50: "EXTRA50INCOMPENSAVEL",
  EXTRA60: "EXTRA60INCOMPENSAVEL",
  EXTRA70: "EXTRA70INCOMPENSAVEL",
};

export function tipoBase(tipo) {
  const base = Object.keys(INCOMPENSAVEL).find(
    (chave) => INCOMPENSAVEL[chave] === tipo
  );
  return base || tipo;
}

export function variante(destino, incompensavel) {
  return incompensavel ? INCOMPENSAVEL[destino] || destino : destino;
}

function paraData(valor) {
  if (valor instanceof Date) {
    return valor;
  }
  const [ano, mes, dia] = String(valor || "")
    .slice(0, 10)
    .split("-")
    .map(Number);
  return ano ? new Date(ano, mes - 1, dia) : null;
}

export function situacaoDia(data, feriado) {
  if (feriado === true) {
    return "FERIADO";
  }
  const diaSemana = paraData(data)?.getDay();
  if (diaSemana === 0) {
    return "DOMINGO";
  }
  if (diaSemana === 6) {
    return "SABADO";
  }
  return "DIA_UTIL";
}

// Precedência: feriado, dia da semana e por fim "todos os dias".
function candidatas(data, feriado) {
  return [
    ...(feriado === true ? ["FERIADO"] : []),
    situacaoDia(data, false),
    "TODOS",
  ];
}

function ordenarFaixas(faixas) {
  return [...faixas].sort((a, b) => {
    const ordem = (a.ordem ?? Infinity) - (b.ordem ?? Infinity);
    if (ordem !== 0 && Number.isFinite(ordem)) {
      return ordem;
    }
    return (a.limiteHoras ?? Infinity) - (b.limiteHoras ?? Infinity);
  });
}

export function faixasAplicaveis(niveis, tipo, data, feriado) {
  const origem = tipoBase(tipo);
  const lista = Array.isArray(niveis) ? niveis : [];
  for (const situacao of candidatas(data, feriado)) {
    const faixas = lista.filter(
      (nivel) => nivel && nivel.origem === origem && nivel.situacao === situacao
    );
    if (faixas.length) {
      return ordenarFaixas(faixas);
    }
  }
  return [];
}

// Distribui minutos de hora extra nas faixas; o excedente da última faixa com limite
// permanece no tipo original.
export function distribuirFaixas(tipo, minutos, faixas) {
  const distribuicao = [];
  if (!(minutos > 0) || !faixas?.length) {
    return distribuicao;
  }
  const incompensavel = tipoBase(tipo) !== tipo;
  let restante = minutos;
  let consumido = 0;
  const adicionar = (destino, quantidade) => {
    const existente = distribuicao.find((item) => item.tipo === destino);
    if (existente) {
      existente.minutos += quantidade;
    } else {
      distribuicao.push({ tipo: destino, minutos: quantidade });
    }
  };
  faixas.forEach((faixa) => {
    if (restante <= 0) {
      return;
    }
    const limite =
      faixa.limiteHoras === null || faixa.limiteHoras === undefined
        ? null
        : Math.round(Number(faixa.limiteHoras) * 60);
    const capacidade =
      limite === null ? restante : Math.max(0, limite - consumido);
    const quantidade = Math.min(restante, capacidade);
    if (quantidade > 0) {
      adicionar(variante(faixa.destino, incompensavel), quantidade);
      restante -= quantidade;
      consumido += quantidade;
    }
  });
  if (restante > 0) {
    adicionar(tipo, restante);
  }
  return distribuicao;
}

export function toleranciaConfigurada(tolerancias, data, feriado) {
  const lista = Array.isArray(tolerancias) ? tolerancias : [];
  for (const situacao of candidatas(data, feriado)) {
    const configuracao = lista.find((item) => item?.situacao === situacao);
    if (configuracao) {
      return configuracao;
    }
  }
  return null;
}

function limitarMinutos(valor) {
  if (valor === null || valor === undefined || valor === "") {
    return TOLERANCIA_PADRAO_MINUTOS;
  }
  return Math.max(0, Math.min(TOLERANCIA_MAXIMA_MINUTOS, Number(valor)));
}

function minutosDoDia(horario) {
  const [horas, minutos] = String(horario || "")
    .split(":")
    .map(Number);
  return Number.isFinite(horas) && Number.isFinite(minutos)
    ? horas * 60 + minutos
    : null;
}

export function variacoesMarcacoes(previstas, realizadas) {
  const previstasMin = (previstas || []).map(minutosDoDia);
  const realizadasMin = (realizadas || []).map(minutosDoDia);
  if (
    !previstasMin.length ||
    !realizadasMin.length ||
    previstasMin.includes(null) ||
    realizadasMin.includes(null)
  ) {
    return null;
  }
  let comparadas;
  if (previstasMin.length === realizadasMin.length) {
    comparadas = previstasMin;
  } else if (
    realizadasMin.length === 2 &&
    previstasMin.length > 2 &&
    previstasMin.length % 2 === 0
  ) {
    comparadas = [previstasMin[0], previstasMin[previstasMin.length - 1]];
  } else {
    return null;
  }
  return realizadasMin.map((realizada, indice) => {
    const diferenca = Math.abs(realizada - comparadas[indice]);
    return Math.min(diferenca, 24 * 60 - diferenca);
  });
}

export function excedeToleranciaMarcacao(variacoes) {
  const total = variacoes.reduce((soma, variacao) => soma + variacao, 0);
  return (
    variacoes.some((variacao) => variacao > TOLERANCIA_MARCACAO_MINUTOS) ||
    total > TOLERANCIA_MAXIMA_MINUTOS
  );
}

// Tolerância efetiva do dia; sem configuração aplica o padrão legal de 10 minutos.
export function toleranciaDia(
  tolerancias,
  data,
  feriado,
  previstas,
  realizadas
) {
  const configuracao = toleranciaConfigurada(tolerancias, data, feriado);
  if (!configuracao) {
    return {
      situacao: null,
      extras: TOLERANCIA_PADRAO_MINUTOS,
      debitos: TOLERANCIA_PADRAO_MINUTOS,
      porMarcacao: false,
      variacoes: null,
      excedeuMarcacao: false,
    };
  }
  let extras = limitarMinutos(configuracao.toleranciaExtrasMinutos);
  let debitos = limitarMinutos(configuracao.toleranciaDebitosMinutos);
  const porMarcacao = configuracao.toleranciaPorMarcacao === true;
  const variacoes = porMarcacao
    ? variacoesMarcacoes(previstas, realizadas)
    : null;
  const excedeuMarcacao = !!variacoes && excedeToleranciaMarcacao(variacoes);
  if (excedeuMarcacao) {
    extras = 0;
    debitos = 0;
  }
  return {
    situacao: configuracao.situacao,
    extras,
    debitos,
    porMarcacao,
    variacoes,
    excedeuMarcacao,
  };
}

// Até a tolerância os minutos são desconsiderados; acima dela conta a totalidade (Súmula 366 TST).
export function aplicarTolerancia(minutos, tolerancia) {
  const limite = tolerancia <= 0 ? 0.5 : tolerancia;
  return minutos > limite ? minutos : 0;
}

function somaPares(horarios) {
  const minutos = (horarios || []).map(minutosDoDia);
  let total = 0;
  for (let i = 0; i + 1 < minutos.length; i += 2) {
    let duracao = minutos[i + 1] - minutos[i];
    if (duracao < 0) {
      duracao += 24 * 60;
    }
    total += duracao;
  }
  return total;
}

export function multiplicadorBancoHoras(tipo, ativo) {
  if (!ativo) {
    return 1;
  }
  return 1 + (PERCENTUAL_TIPO[tipo] || 0) / 100;
}

// Simulação completa de um dia: marcações -> tolerância -> extras/débitos -> faixas -> folha e banco.
export function simularDia({
  data,
  feriado,
  previstas,
  realizadas,
  tipoExtra = "EXTRA50",
  niveis = [],
  tolerancias = [],
  rubricas = [],
  apuracoesBh = [],
  multiplicadorBh = false,
}) {
  const tolerancia = toleranciaDia(
    tolerancias,
    data,
    feriado,
    previstas,
    realizadas
  );
  // Mesma estrutura do cálculo: extras pela jornada excedente, atraso pela primeira entrada e
  // horas a compensar pela jornada não cumprida descontado o atraso.
  const diferenca = somaPares(realizadas) - somaPares(previstas);
  const extras = aplicarTolerancia(Math.max(0, diferenca), tolerancia.extras);
  const entradaPrevista = minutosDoDia((previstas || [])[0]);
  const entradaRealizada = minutosDoDia((realizadas || [])[0]);
  const atrasoBruto =
    entradaPrevista === null || entradaRealizada === null
      ? 0
      : Math.max(0, entradaRealizada - entradaPrevista);
  const atraso = aplicarTolerancia(atrasoBruto, tolerancia.debitos);
  const compensar = aplicarTolerancia(
    Math.max(0, -diferenca - atraso),
    tolerancia.debitos
  );
  const debitos = atraso + compensar;
  const faixas = faixasAplicaveis(niveis, tipoExtra, data, feriado);
  const distribuicao = faixas.length
    ? distribuirFaixas(tipoExtra, extras, faixas)
    : extras > 0
    ? [{ tipo: tipoExtra, minutos: extras }]
    : [];
  const tiposBh = (apuracoesBh || []).map((item) => item?.tipo);
  const linhas = distribuicao.map((item) => {
    const noBanco =
      tiposBh.includes(item.tipo) || tiposBh.includes(tipoBase(item.tipo));
    return {
      ...item,
      percentual: PERCENTUAL_TIPO[item.tipo] ?? null,
      rubrica:
        (rubricas || []).find((rubrica) => rubrica?.tipo === item.tipo)
          ?.rubrica || null,
      noBanco,
      minutosBanco: noBanco
        ? Math.round(
            item.minutos * multiplicadorBancoHoras(item.tipo, multiplicadorBh)
          )
        : 0,
    };
  });
  return {
    situacao: situacaoDia(data, feriado),
    tolerancia,
    diferenca,
    extras,
    atraso,
    compensar,
    debitos,
    faixasAplicadas: faixas.length > 0,
    linhas,
  };
}

const TIPOS_DEVEDORES = ["COMPENSAR", "ATRASO", "FALTA_INJUSTIFICADA"];

// Separa as apurações diárias do cálculo por tipo de dia (úteis, sábados, domingos e feriados).
export function resumoPorTipoDia(dias) {
  const resumo = {};
  ["DIA_UTIL", "SABADO", "DOMINGO", "FERIADO"].forEach((situacao) => {
    resumo[situacao] = {
      situacao,
      dias: 0,
      diasComExtras: 0,
      extras: {},
      totalExtras: 0,
      devedoras: 0,
    };
  });
  (Array.isArray(dias) ? dias : []).forEach((dia) => {
    if (!dia?.data) {
      return;
    }
    const linha = resumo[situacaoDia(dia.data, dia.feriado === true)];
    linha.dias += 1;
    let possuiExtras = false;
    (dia.pontoCalculoDiaApuracao || []).forEach((apuracao) => {
      const horas = Number(apuracao?.hora) || 0;
      if (horas <= 0) {
        return;
      }
      if (PERCENTUAL_TIPO[apuracao.tipo] !== undefined) {
        linha.extras[apuracao.tipo] =
          (linha.extras[apuracao.tipo] || 0) + horas;
        linha.totalExtras += horas;
        possuiExtras = true;
      } else if (TIPOS_DEVEDORES.includes(apuracao.tipo)) {
        linha.devedoras += horas;
      }
    });
    if (possuiExtras) {
      linha.diasComExtras += 1;
    }
  });
  return Object.values(resumo);
}

// Faixas aplicadas num dia do cálculo: agrupa as horas extras do dia por percentual.
export function faixasDoDia(dia) {
  const extras = (dia?.pontoCalculoDiaApuracao || []).filter(
    (apuracao) =>
      PERCENTUAL_TIPO[apuracao?.tipo] !== undefined && Number(apuracao.hora) > 0
  );
  return extras
    .map((apuracao) => ({
      tipo: apuracao.tipo,
      horas: Number(apuracao.hora),
      percentual: PERCENTUAL_TIPO[apuracao.tipo],
    }))
    .sort((a, b) => a.percentual - b.percentual);
}

export function validarNiveis(niveis) {
  const erros = [];
  const grupos = {};
  (niveis || []).forEach((nivel) => {
    if (
      !TIPOS_HORA_EXTRA.includes(nivel?.origem) ||
      !TIPOS_HORA_EXTRA.includes(nivel?.destino) ||
      !SITUACOES_DIA.includes(nivel?.situacao) ||
      (nivel.limiteHoras !== null &&
        nivel.limiteHoras !== undefined &&
        !(Number(nivel.limiteHoras) > 0))
    ) {
      erros.push("Faixa_horas_invalida");
      return;
    }
    const chave = `${nivel.origem}:${nivel.situacao}`;
    (grupos[chave] = grupos[chave] || []).push(nivel);
  });
  Object.values(grupos).forEach((grupo) => {
    const ordenadas = [...grupo].sort(
      (a, b) => (a.ordem ?? Infinity) - (b.ordem ?? Infinity)
    );
    let anterior = 0;
    ordenadas.forEach((nivel, indice) => {
      const limite = nivel.limiteHoras;
      if (
        (limite === null || limite === undefined) &&
        indice < ordenadas.length - 1
      ) {
        erros.push("Faixa_restante_deve_ser_ultima");
      } else if (
        limite !== null &&
        limite !== undefined &&
        Number(limite) <= anterior
      ) {
        erros.push("Faixa_limites_crescentes");
      }
      if (limite !== null && limite !== undefined) {
        anterior = Number(limite);
      }
    });
  });
  return [...new Set(erros)];
}
